const ALLOWED_MIME = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const ALLOWED_EXT = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif']);

export type ClipboardImageSource = {
  files?: ArrayLike<File> | null;
  items?: ArrayLike<{
    kind: string;
    type: string;
    getAsFile: () => File | null;
  }> | null;
};

function fileExtension(name: string): string {
  const parts = String(name || '').split('.');
  return parts.length > 1 ? String(parts.pop() || '').toLowerCase() : '';
}

export function isAllowedReferenceImageFile(file: Pick<File, 'name' | 'type'>): boolean {
  const mime = String(file.type || '').toLowerCase();
  if (ALLOWED_MIME.has(mime)) return true;
  return ALLOWED_EXT.has(fileExtension(file.name));
}

function fileKey(file: File): string {
  return [file.name, file.type, file.size].join('|');
}

export function clipboardHasFilePayload(source: ClipboardImageSource | null | undefined): boolean {
  if (!source) return false;
  if (source.files && source.files.length > 0) return true;
  if (!source.items) return false;
  for (let index = 0; index < source.items.length; index += 1) {
    if (source.items[index].kind === 'file') return true;
  }
  return false;
}

export function readClipboardImageFiles(source: ClipboardImageSource | null | undefined): File[] {
  if (!source) return [];

  const found: File[] = [];
  const seen = new Set<string>();

  const pushIfAllowed = (file: File | null) => {
    if (!file || !isAllowedReferenceImageFile(file)) return;
    const key = fileKey(file);
    if (seen.has(key)) return;
    seen.add(key);
    found.push(file);
  };

  if (source.files) {
    for (let index = 0; index < source.files.length; index += 1) {
      pushIfAllowed(source.files[index]);
    }
  }

  // Chrome/Edge on Windows expose the same screenshot in both files and items.
  // getAsFile() returns a new File, so skip items when files already yielded images.
  if (found.length > 0) {
    return found;
  }

  if (source.items) {
    for (let index = 0; index < source.items.length; index += 1) {
      const item = source.items[index];
      if (item.kind !== 'file') continue;
      pushIfAllowed(item.getAsFile());
    }
  }

  return found;
}
